import qs from 'qs';
import { convertLocaleType } from 'src/utils/convertLocaleType';
import { getAccessToken, getRefreshToken, setAuthCookies } from 'src/utils/cookies';
import { getLocaleFromUrl } from 'src/utils/subdomain';
import { microTemplate } from '../../utils/microTemplate';
import { updateSession } from './sessionApi';

const defaultHeaders = () => {
  const locale = convertLocaleType(getLocaleFromUrl());
  const accessToken = getAccessToken();
  const authrizationHeader: { Authroization: string } | {} =
    accessToken != null
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : {};

  return {
    ...authrizationHeader,
    'Accept-Language': locale,
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-WAPI-KEY': process.env.NEXT_PUBLIC_WAPI_KEY || '',
  };
};

const fetchWithTimeout = async (url: string, request: RequestInit, timeout: number) => {
  const msec = timeout * 1000;

  // SSG時で使おうとするとAbortControllerがundefinedになり使えない
  const abortController = new AbortController();
  const signal = abortController.signal;

  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, msec);

  const response = await window.fetch(url, { ...request, signal });

  clearTimeout(timeoutId);

  return response;
};

type AnyHash = { [key: string]: any };

type Params = {
  baseURI: string;
  path: string;
  params?: AnyHash;
  body?: string;
  headers?: AnyHash;
  options?: AnyHash;
  timeout?: number;
  cache?: number;
  resource?: string;
};

type SendParams = {
  method: string;
  baseURI: string;
  path: string;
  params?: AnyHash;
  body?: string;
  headers?: AnyHash;
  options?: AnyHash;
  timeout?: number;
  cache?: number;
  resource?: string;
};

const defaultArrayFormat = 'brackets';
const defaultTimeout = 30;

const expandPathTemplate = (sendParams: SendParams): SendParams => {
  let path = sendParams.path;
  let params = sendParams.params;

  if (params != null) {
    const processed = microTemplate({ template: path, params });

    path = processed.result;
    params = processed.params;
  }

  if (sendParams.resource != null && params != null) {
    params = { [sendParams.resource]: params };
  }

  return {
    ...sendParams,
    path,
    params,
  };
};

const buildRequestBody = (sendParams: SendParams): SendParams => {
  let body: undefined | string;

  if (typeof sendParams.body == 'string') {
    body = sendParams.body;
  }

  if (sendParams.params != null && (sendParams.method == 'POST' || 'DELETE' || 'PATCH')) {
    body = JSON.stringify(sendParams.params);
  }

  if (typeof body == 'undefined' && sendParams.method == 'POST') {
    body = '';
  }

  return {
    ...sendParams,
    body,
  };
};

const buildQueryString = (sendParams: SendParams): SendParams => {
  if (sendParams.method == 'GET' && typeof sendParams.params == 'object') {
    const queries = qs.stringify(sendParams.params, {
      arrayFormat: defaultArrayFormat,
    });

    const path = `${sendParams.path}?${queries}`;

    return {
      ...sendParams,
      path,
    };
  }

  return sendParams;
};

const rebuildSendParams = (sendParams: SendParams): SendParams => {
  let params = sendParams;

  params = expandPathTemplate(params);
  params = buildRequestBody(params);
  params = buildQueryString(params);

  return params;
};

const buildUrl = (sendParams: SendParams): string => {
  return sendParams.baseURI + sendParams.path;
};

const buildRequest = (sendParams: SendParams): RequestInit => {
  const { method, options, headers, body } = sendParams;

  const request: RequestInit = {
    method,
    ...options,
    headers: {
      ...defaultHeaders(),
      ...headers,
    },
    body,
  };

  return request;
};

const reauthorize = async () => {
  const refreshToken = getRefreshToken();

  if (refreshToken == null) {
    return false;
  }

  const response = await updateSession(refreshToken);

  if (response != null) {
    // update sessions cookie
    setAuthCookies(response.secure_user.access_token, response.secure_user.refresh_token);

    return true;
  }

  return false;
};

const sendRequest = async <ResponseType>(sendParams: SendParams, isRetry = false): Promise<ResponseType> => {
  const params = rebuildSendParams(sendParams);

  const url = buildUrl(params);
  const request = buildRequest(params);
  const timeout = params.timeout ? params.timeout : defaultTimeout;

  const response = await fetchWithTimeout(url, request, timeout);

  if (response.status === 401 && !isRetry) {
    const isInUpdateSession = sendParams.method === 'PATCH' && sendParams.path === '/v1/auth/sessions';

    if (!isInUpdateSession) {
      // Authorization Error
      const result = await reauthorize();
      if (result) {
        sendRequest(sendParams, true);
      }
    }
  }

  return response.json();
};

const getRequest = async <ResponseType>(params: Params) => {
  const method = 'GET';

  return sendRequest<ResponseType>({ method, ...params });
};

const postRequest = async <ResponseType>(params: Params) => {
  const method = 'POST';

  return sendRequest<ResponseType>({ method, ...params });
};

const patchRequest = async <ResponseType>(params: Params) => {
  const method = 'PATCH';

  return sendRequest<ResponseType>({ method, ...params });
};

const putRequest = async <ResponseType>(params: Params) => {
  const method = 'PUT';

  return sendRequest<ResponseType>({ method, ...params });
};

const deleteRequest = async <ResponseType>(params: Params) => {
  const method = 'DELETE';

  return sendRequest<ResponseType>({ method, ...params });
};

export { deleteRequest, getRequest, patchRequest, postRequest, putRequest };
